import { FC, useCallback, useEffect, useState } from "react";
import { GridApi } from "ag-grid-community";
import { Chip, Stack, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { AgGridAllFiltersChip } from "@/components/pages/common/ag-grid/components/filters/AgGridAllFiltersChip";
import { OffHoursCreateButton } from "@/components/pages/automations/common/components/OffHoursCreateButton";
import { AgGridGlobalSearch } from "@/components/pages/common/ag-grid/components/global-search/AgGridGlobalSearch";
import { AGGridColumnsToolPanel } from "@/components/pages/common/ag-grid/components/AGGridColumnsToolPanel";

interface OffHoursGridToolbarProps {
  gridApi: GridApi | null;
  offHoursCount?: number;
}

export const OffHoursGridToolbar: FC<OffHoursGridToolbarProps> = ({
  gridApi,
  offHoursCount,
}) => {
  const [searchParams] = useSearchParams();
  const searchFromPath = searchParams.get("search") ?? null;
  const [search, setSearch] = useState<string>("");
  const [gridRowCount, setGridRowCount] = useState<number>(0);

  const searchChangeHandler = useCallback(
    (keyword: string | number) => {
      gridApi?.setGridOption("quickFilterText", `${keyword}`);
      setSearch(`${keyword}`);
    },
    [gridApi],
  );

  useEffect(() => {
    searchFromPath && searchChangeHandler(searchFromPath);
  }, [searchFromPath, searchChangeHandler]);

  useEffect(() => {
    if (!gridApi) return;

    const updateRowCount = () => {
      const count = gridApi.getDisplayedRowCount();
      setGridRowCount(count);
    };

    updateRowCount();

    gridApi.addEventListener("rowDataUpdated", updateRowCount);
    gridApi.addEventListener("filterChanged", updateRowCount);

    return () => {
      gridApi.removeEventListener("rowDataUpdated", updateRowCount);
      gridApi.removeEventListener("filterChanged", updateRowCount);
    };
  }, [gridApi]);

  const isFiltered =
    offHoursCount !== undefined &&
    gridRowCount > 0 &&
    offHoursCount > gridRowCount;
  const displayText = isFiltered ? gridRowCount : offHoursCount;

  return (
    <Stack
      p={1.5}
      spacing={1.5}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="body2" color="text.secondary" component="div">
          {isFiltered && (
            <Typography variant="inherit" component="span">
              Filtered Result:{" "}
            </Typography>
          )}
          <Chip
            size="small"
            variant="filled"
            sx={{
              borderRadius: 1,
              color: "text.secondary",
              fontWeight: "bold",
            }}
            label={`${displayText} Off Hours`}
          />
        </Typography>

        <AgGridAllFiltersChip gridApi={gridApi} />
      </Stack>

      <Stack direction="row" alignItems="center" spacing={2}>
        <OffHoursCreateButton />

        <AgGridGlobalSearch gridApi={gridApi} searchValue={search} />

        <AGGridColumnsToolPanel gridApi={gridApi} />
      </Stack>
    </Stack>
  );
};
